import { MenuItem, TextField } from '@freighthub/silhouette-core'
import { toPairs } from 'lodash-es'
import { ChangeEvent, FC, useEffect, useMemo, useRef, useState } from 'react'
import { ISO } from 'service/ISO'
import { logError } from 'utils/logger'

import { TCarriageMode, TCarriageType } from '../Carriage'
import { useQuoteService } from '../QuoteService'
import {
  AddressType,
  BookingParty,
  getIncotermsList,
  getPreferredIncoTerms,
  IncoTerm,
  IncoTermMetadata,
} from './incotermsMeta'

type IncotermSelectorProps = {
  selectedIncoTerm?: ISO.IncoTermsCode
  handleIncoTermChange: (code: ISO.IncoTermsCode | null) => void
  fullWidth?: boolean
  required?: boolean
  bookingParty: BookingParty | null
  originType: AddressType
  destinationType: AddressType
  transportType: TCarriageType
  transportModes: TCarriageMode[]
  updatePreferredIncotermUntilManualChange?: boolean
}

export const IncotermSelector: FC<IncotermSelectorProps> = ({
  selectedIncoTerm,
  handleIncoTermChange,
  fullWidth = true,
  required = false,
  bookingParty,
  originType,
  destinationType,
  transportType,
  transportModes,
  updatePreferredIncotermUntilManualChange = false,
}) => {
  const [incoTermMetadata, setIncotermMetadata] = useState<IncoTermMetadata[]>([])
  const updatePreferredIncoterm = useRef(updatePreferredIncotermUntilManualChange)
  const quoteService = useQuoteService()

  const onChange = (e: ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => {
    handleIncoTermChange(e.target.value)
    // Stop updating the preferred incoterm once the user has made a selection
    updatePreferredIncoterm.current = false
  }

  useEffect(() => {
    const fetchIncotermsMetadata = async () => {
      try {
        const incotems = await quoteService.getIncotermsMetadata()
        setIncotermMetadata(incotems)
      } catch (error) {
        // @ts-expect-error TS(2345) FIXME: Argument of type 'unknown' is not assignable to pa... Remove this comment to see the full error message
        logError(error)
      }
    }

    fetchIncotermsMetadata()
  }, [])

  const incoTerms = useMemo(() => {
    if (incoTermMetadata.length === 0) {
      return toPairs(ISO.IncoTermCodesDescriptions).map(
        ([code, { description }]) => ({ code, description }) as IncoTerm
      )
    }

    return getIncotermsList(incoTermMetadata)
  }, [incoTermMetadata])

  const transportModeKey = transportModes.sort().join(',')
  const preferredIncoterm = useMemo(() => {
    if (bookingParty === null) {
      return null
    }
    return getPreferredIncoTerms(incoTermMetadata, {
      bookingParty,
      originType,
      destinationType,
      transportType,
      transportModes,
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [incoTermMetadata, bookingParty, originType, destinationType, transportType, transportModeKey])

  // update selected incoterm when preferred incoTerm changes
  useEffect(() => {
    if (updatePreferredIncoterm.current) {
      handleIncoTermChange(preferredIncoterm)
    }
  }, [preferredIncoterm])

  return (
    <TextField
      // Adding translate=no here due to a react issue where the page would crash if the user had
      // a translation extension enabled. Error: "NotFoundError: Failed to execute 'removeChild' on 'Node'"
      // https://github.com/facebook/react/issues/17256
      translate="no"
      value={selectedIncoTerm}
      onChange={onChange}
      select
      fullWidth={fullWidth}
      required={required}
      inputProps={{
        'aria-label': 'Incoterm',
      }}
    >
      {incoTerms.map(({ code, description }) => (
        <MenuItem value={code} key={code}>
          <span>
            {ISO.IncoTermCodesDescriptions[code].abbreviation} - {description}
          </span>
        </MenuItem>
      ))}
    </TextField>
  )
}
